/**
 * @fileoverview ProspectPuffin Terms of Service registry data.
 *
 * AUTHORED 2026-07-14 (AISEO-001) — ProspectPuffin shipped LIVE with no terms of service.
 *
 * ⚠️ PENDING LEGAL REVIEW FOR ACCURACY. Drafted from the platform's documented behaviour, not
 * from counsel. Two clauses carry real weight and should be read closely:
 *
 *   - §5 Acceptable Use / §6 Outbound Contact — the customer is the SENDER OF RECORD for
 *     outreach they initiate. ProspectPuffin enforces a TCPA/DNC gate, but a compliance gate is
 *     not a compliance guarantee, and the liability allocation here is the load-bearing term.
 *   - §8 Prospect Data — the customer directs which public-record and listing sources are
 *     ingested and who is contacted. That makes them the controller of that processing.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'prospect',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'ProspectPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-07-14',
    contactEmail: 'legal@prospectpuffin.com',
    canonicalUrl: 'https://prospectpuffin.com/terms',
    ogImage: 'https://prospectpuffin.com/og-image.jpg',
    description:
      'ProspectPuffin terms of service. Review the terms governing use of our business-development platform.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName}, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our services. These terms apply to all users, including businesses and individuals who access {productName} for business development, account reactivation, and commercial prospecting.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides business-development tooling for commercial service operators. The platform identifies dormant commercial accounts worth reactivating, ingests and qualifies net-new commercial opportunities from public records and business listings, assembles prioritized worklists, and records the outreach activity you carry out against them.',
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized access.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '4. Subscription and Billing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is offered on a subscription basis as an add-on capability. Contact us for pricing details. You may cancel your subscription at any time. No refunds are provided for partial billing periods.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '5. Acceptable Use',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'You agree not to misuse {productName}. You may not use the platform to send unlawful, deceptive, or harassing communications; to contact recipients you are not lawfully permitted to contact; to circumvent or disable the consent and Do-Not-Call gate; to scrape or resell prospect data; or to attempt to gain unauthorized access to the platform or another company’s data.',
        },
        {
          type: 'paragraph',
          text: 'You may not use prospect information obtained through the platform for any purpose other than the business-to-business commercial outreach for which it was provided to you.',
        },
      ],
    },
    {
      id: 'outbound-contact-and-compliance',
      title: '6. Outbound Contact and Compliance',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You are the sender of record for all outreach you initiate through {productName}. You decide which recipients to contact and what to say to them, and you are responsible for ensuring that each communication complies with the law applicable to you — including the Telephone Consumer Protection Act, the CAN-SPAM Act, state telemarketing and Do-Not-Call statutes, and any equivalent law in the recipient’s jurisdiction.',
        },
        {
          type: 'paragraph',
          text: '{productName} enforces a consent and Do-Not-Call gate on outbound email and SMS and logs every send and every block decision. This gate is a control, not a warranty: it does not constitute legal advice, and it does not transfer to us the legal responsibility for communications you choose to send. If you disable or bypass a compliance control, you assume the resulting liability.',
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '7. Intellectual Property',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'All content, features, and functionality of {productName} — including software, scoring models, design, and trademarks — are owned by {legalEntity} and protected by intellectual-property law. You may not copy, modify, distribute, or reverse-engineer any part of the platform without our written permission.',
        },
      ],
    },
    {
      id: 'your-data-and-prospect-data',
      title: '8. Your Data and Prospect Data',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'You retain ownership of the customer and account records you synchronize into {productName}. You grant us a limited license to process them solely to provide the service to you. Our handling of personal information is described in our Privacy Policy.',
        },
        {
          type: 'paragraph',
          text: 'Prospect records assembled from public records and business listings are provided to you for your commercial outreach. You direct which sources are ingested and which prospects are contacted, and you are the controller of that processing. You are responsible for honoring any access, correction, deletion, or objection request you receive from a business contact in respect of outreach you sent.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '9. Limitation of Liability',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'To the maximum extent permitted by law, {legalEntity} is not liable for indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or business opportunity, arising from your use of {productName}. Scoring and qualification outputs are decision support and are provided without warranty as to their accuracy or suitability; commercial decisions you take on the basis of them remain yours.',
        },
      ],
    },
    {
      id: 'termination',
      title: '10. Termination',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may suspend or terminate your access if you breach these terms, including any misuse of outbound contact. You may terminate your account at any time. On termination we will delete or return your data on request, subject to any retention the law requires of us and to the outreach records we must retain to evidence compliance.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '11. Changes to Terms',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update these terms from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated terms.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '12. Contact Us',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these terms, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
